import React, { useState } from "react";
import { motion } from "framer-motion";
import { 
  Car, Truck, Building2, Users, Briefcase, 
  Clock, Bell, Phone
} from "lucide-react";
import { Header, Footer } from "../components/common";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const BUSINESS_TYPES = [
  { id: "dealer", label: "Car Dealerships", icon: Car },
  { id: "fleet", label: "Fleet Operators", icon: Truck },
  { id: "rideshare", label: "Rideshare Drivers", icon: Users },
  { id: "tradie", label: "Tradies & Business", icon: Briefcase }
];

const TradePage = () => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleNotify = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/trade-inquiries`, null, {
        params: { email, business_type: "notify" }
      });
      toast.success("You'll be notified when Trade packages launch!");
      setEmail("");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 relative" data-testid="trade-page">
      <Header />
      
      {/* Background content (blurred) */}
      <div className="absolute inset-0 pt-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 py-32 blur-sm opacity-30">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Trade Packages</h2>
            <p className="text-slate-400">Special rates for businesses</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="glass rounded-2xl p-6 h-64" />
            ))}
          </div>
        </div>
      </div>

      {/* Coming Soon Overlay */}
      <div className="fixed inset-0 z-40 flex items-center justify-center" data-testid="coming-soon-overlay">
        {/* Blur backdrop */}
        <div className="absolute inset-0 backdrop-blur-md bg-slate-950/80" />
        
        {/* Coming Soon Content */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="relative z-50 text-center px-6 max-w-xl mx-auto"
        >
          <div className="inline-flex items-center gap-2 bg-cyan-400/20 border border-cyan-400/50 rounded-full px-6 py-3 mb-6">
            <Clock className="w-5 h-5 text-cyan-400 animate-pulse" />
            <span className="text-cyan-400 font-bold text-lg">Coming Soon</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Trade & Business <span className="gradient-text">Packages</span>
          </h1>
          
          <p className="text-slate-300 text-lg mb-6">
            Special rates for car dealers, fleet operators, rideshare drivers, and tradies.
          </p>
          
          <p className="text-cyan-400 font-bold text-2xl mb-8">
            Up to 38% off coming very soon!
          </p>

          {/* Business type icons */}
          <div className="flex justify-center gap-6 mb-8">
            {BUSINESS_TYPES.map((type) => (
              <div key={type.id} className="text-center">
                <div className="w-14 h-14 rounded-full bg-slate-800/80 flex items-center justify-center mx-auto mb-2">
                  <type.icon className="w-7 h-7 text-cyan-400" />
                </div>
                <p className="text-slate-400 text-xs">{type.label}</p>
              </div>
            ))}
          </div>
          
          <form onSubmit={handleNotify} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto mb-6">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="flex-1 bg-slate-800 border border-slate-700 rounded-full px-5 py-3 text-white focus:border-cyan-400 focus:outline-none"
              data-testid="coming-soon-email"
            />
            <Button
              type="submit"
              disabled={submitting}
              className="btn-primary-glow text-slate-900 font-semibold px-6 py-3 rounded-full whitespace-nowrap"
              data-testid="coming-soon-notify-btn"
            >
              <Bell className="w-4 h-4 mr-2" />
              {submitting ? "..." : "Notify Me"}
            </Button>
          </form>
          
          <p className="text-slate-500 text-sm">
            Or call us for trade inquiries:{" "}
            <a href="tel:0478054648" className="text-cyan-400 hover:underline flex items-center justify-center gap-1 mt-2">
              <Phone className="w-4 h-4" />
              0478 054 648
            </a>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default TradePage;
